import { Controller, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { MultiSelect } from 'components/form-elements/selects/MultiSelect';
import { useModal } from 'components/../store/modals';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetUsers } from 'services/2n/get-all-users';
import { useUpdateGroup } from 'services/2n/groups';
import { DeleteGroup } from '../DeleteGroup';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import groupFormSchema from '../validation';
import styles from './styles.module.scss';

export const GroupForm = () => {

  const { selected, updateSelected } = useAccessManagement2NModal();
  const selectedGroup = selected.group;
  const { users } = useGetUsers();
  const usersInThisGroup = users.map((user) => ({ value: user.Id, label: user.Name, email: user.Email }));
  const selectedGroupMembers = (selectedGroup && selectedGroup.Members)
    ? selectedGroup.Members.map((m) => ({ value: m.Id, label: m.Name, email: m.Email }))
    : [];
  const { openModal } = useModal();
  const intl = useIntl();

  const { control, handleSubmit, formState, reset } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(groupFormSchema),
    defaultValues: {
      name: selectedGroup && selectedGroup.Name ? selectedGroup.Name : '',
      usersInThisGroup: selectedGroupMembers,
    },
  });

  useEffect(() => {
    if (!selectedGroup) return;
    reset({
      name: selectedGroup.Name || '',
      usersInThisGroup: selectedGroupMembers,
    });
  }, [selectedGroup]);

  const { errors } = formState || {};

  const { mutateAsync: updateGroup, isLoading: isUpdating } = useUpdateGroup();

  const onSubmit = async (data) => {
    if (!selectedGroup) return;
    const payload = {
      name: data.name,
      users: (data.usersInThisGroup || []).map((u) => ({ id: u.value })),
    };
    await updateGroup({ id: selectedGroup.Id, group: payload });
  };

  const formatError = (msg) => {
    if (!msg) return '';
    try {
      // assume schema returns message keys; attempt intl lookup, fallback to raw
      return typeof msg === 'string' ? intl.formatMessage({ id: msg }) : String(msg);
    } catch (e) {
      return String(msg);
    }
  };

  return (
    <div className={styles.container}>
      <Brikk.Heading
        heading="accessGroupsEdit"
      />
      <ShouldRender condition={selectedGroup && Object.hasOwn(selectedGroup, 'Name')}>
        <Text
          element="h2"
          thin
          xl
          size={20}
        >
          {String((selectedGroup && selectedGroup.Name) || '')}
        </Text>
      </ShouldRender>
      {selectedGroup ? (
 
        <Form
          className={styles.formContainer}
          onSubmit={handleSubmit(onSubmit)}
        >

          <div className={styles.formWrapper}>
            <Grid
              noPadding
              oneColumn
            >
              <div className={styles.form}>
             
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      labelIntl="accessGroupsName"
                      id="name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      hasError={!!(errors && errors.name)}
                    />
                  )}
                />
                <ShouldRender condition={errors && errors.name}>
                  <Message negative>
                    <Message.Content>
                      <Text
                        error
                      >
                        {formatError(errors.name && errors.name.message)}
                      </Text>
                    </Message.Content>
                  </Message>
                </ShouldRender>

                <Controller
                  name="usersInThisGroup"
                  control={control}
                  render={({ field }) => {

                    const handleChange = (newValue) => {
                      const oldValue = field.value || [];
                      const newArr = newValue || [];

                      const removed = oldValue.filter(oldItem => !newArr.some(n => n.value === oldItem.value));
                      if (removed.length > 0) {
                        openModal({
                          modalType: 'ConfirmationModal',
                          modalProps: {
                            title: intl.formatMessage({ id: 'removeUser' }),
                            content: intl.formatMessage(
                              { id: 'accessGroupsRemoveUserConfirm' },
                            ),
                            onConfirm: () => field.onChange(newValue),
                          },
                        });
                      } else {
                        field.onChange(newValue);
                      }
                    };

                    return (
                      <>
                        <MultiSelect
                          options={usersInThisGroup}
                          title="accessGroupsUsersInGroup"
                          value={field.value}
                          onChange={handleChange}
                        />
                        <ShouldRender condition={errors && errors.usersInThisGroup}>
                          <Message negative>
                            <Message.Content>
                              <Text
                                error
                              >
                                {formatError(errors.usersInThisGroup && errors.usersInThisGroup.message)}
                              </Text>
                            </Message.Content>
                          </Message>
                        </ShouldRender>
                      </>
                    );
                  }}
                />

              </div>
            </Grid>
          </div>

          <div className={styles.formButtons}>
            <Button
              basic
              name="cancel-group-details"
              type="button"
              disabled={isUpdating}
              onClick={() => updateSelected('group', null)}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              type="submit"
              name="submit-group-details"
              disabled={isUpdating}
              loading={isUpdating}
            >
              <FormattedMessage id="saveChanges" />
            </Button>
          </div>

          <ShouldRender condition={selectedGroup}>
            <DeleteGroup />
          </ShouldRender>

        </Form>

      ) : (
        <Text
          secondary
          center
          intl="accessGroupsNoGroupSelected"
          size={16}
        />
      )}
    </div>
  );
};
